import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { uploadImage } from '../../api/apiHooks'
import { Inputs } from '../GenerateMentor/GenerateMentorForm/GenerateMentorForm'

interface IImageUploaderProps {
  wrapperClassName?: string
}

const ImageUploader: React.FC<IImageUploaderProps> = memo(
  ({ wrapperClassName = '' }) => {
    const lastUrlRef = useRef('')
    const lastResultRef = useRef<true | string>(true)
    const inputRef = useRef<HTMLInputElement>(null)
    const {
      register,
      setValue,
      formState: { errors },
    } = useFormContext<Inputs>()

    const [url, setUrl] = useState('')
    const [isUploading, setIsUploading] = useState(false)

    const validateImage = useCallback(async (value: string) => {
      if (!value) return true
      if (value === lastUrlRef.current) return lastResultRef.current
      try {
        const resp = await fetch(value, { method: 'HEAD' })
        const type = resp.headers.get('Content-Type')

        const ok = resp.ok && type?.startsWith('image/')
        lastUrlRef.current = value
        lastResultRef.current = ok ? true : 'Invalid image URL'
        return lastResultRef.current
      } catch {
        lastUrlRef.current = value
        lastResultRef.current = 'Invalid image URL'
        return 'Invalid image URL'
      }
    }, [])

    useEffect(() => {
      setValue('image', url, { shouldDirty: true, shouldValidate: true })
    }, [url, setValue])

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) =>
      setUrl(e.target.value)

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      setIsUploading(true)
      try {
        const { config } = await uploadImage(file)
        const uploadedUrl = config?.url ?? ''
        setUrl(uploadedUrl)
      } catch (err) {
        console.error('Error uploading image:', err)
        setUrl('')
      } finally {
        setIsUploading(false)
      }
    }

    return (
      <>
        <div className="flex gap-4 items-center">
          <div className={`w-full ${wrapperClassName} !mb-0`}>
            <input
              {...register('image', { validate: validateImage })}
              placeholder="https://example.com/image.jpg"
              className="w-full border border-gray-300 rounded p-2 text-black"
              disabled={isUploading}
              value={url}
              onChange={handleUrlChange}
            />
          </div>
          <div>or</div>
          <div className="flex-shrink-0">
            <label className="btn btn-secondary cursor-pointer !h-[40px]">
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png"
                ref={inputRef}
                disabled={isUploading}
                onChange={handleFileChange}
              />
              {isUploading ? (
                <>
                  <AnimatedSpinner className="me-2" /> Uploading...
                </>
              ) : (
                'Upload Image'
              )}
            </label>
          </div>
        </div>

        {url && !errors.image && (
          <img
            src={url}
            alt="Preview"
            className="rounded-md mt-1 w-20 h-auto max-h-20 object-contain"
          />
        )}

        {errors.image && (
          <p className="text-sm text-red-500">{errors.image.message}</p>
        )}
      </>
    )
  },
)

export { ImageUploader }
